import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const isBrowser =
    typeof window !== 'undefined' && typeof document !== 'undefined';

  const safeGetStoredTheme = useCallback((): Theme | null => {
    if (!isBrowser) return null;
    try {
      return (window.localStorage.getItem(storageKey) as Theme | null) ?? null;
    } catch {
      // Some environments (private mode, embedded contexts) can block localStorage.
      return null;
    }
  }, [isBrowser, storageKey]);

  const safeSetStoredTheme = useCallback(
    (nextTheme: Theme) => {
      if (!isBrowser) return;
      try {
        window.localStorage.setItem(storageKey, nextTheme);
      } catch {
        // Best effort only — theme switching should still work without persistence.
      }
    },
    [isBrowser, storageKey],
  );

  const [theme, setThemeState] = useState<Theme>(() => {
    if (!isBrowser) return defaultTheme;
    return safeGetStoredTheme() || defaultTheme;
  });

  const windowQuery = isBrowser
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

  const darkModeChange = useCallback(
    (e: MediaQueryListEvent) => {
      if (!isBrowser || !windowQuery) return;
      const root = window.document.documentElement;

      if (theme === 'system') {
        const systemTheme = e.matches ? 'dark' : 'light';
        setBodyTheme(root, systemTheme);
      }
    },
    [theme, windowQuery, isBrowser],
  );

  function setBodyTheme(root: HTMLElement, systemTheme: string) {
    root.classList.remove('light', 'dark');

    root.classList.add(systemTheme);
  }

  useEffect(() => {
    if (!isBrowser || !windowQuery) return;

    const root = window.document.documentElement;
    windowQuery.addEventListener('change', darkModeChange);

    if (theme !== 'system') {
      setBodyTheme(root, theme);
    } else {
      const systemTheme = windowQuery.matches ? 'dark' : 'light';
      setBodyTheme(root, systemTheme);
    }

    return () => {
      windowQuery.removeEventListener('change', darkModeChange);
    };
  }, [darkModeChange, theme, windowQuery, isBrowser]);

  const value = {
    theme,
    setTheme: (nextTheme: Theme) => {
      safeSetStoredTheme(nextTheme);
      setThemeState(nextTheme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
